import { useCallback, useEffect, useRef, useState } from 'react';
import { Title, useGetList, useNotify } from 'react-admin';
import {
  Backdrop,
  Box,
  Button,
  CircularProgress,
  LinearProgress,
  Pagination,
  Typography,
} from '@mui/material';

import { useTitle } from '@src/hooks/useTitle';
import { getCurrentPartner } from '@src/providers/dataProvider/shared/partner';
import { getCatalogRules, previewCatalogRules, saveCatalogRules } from '@src/apis/reware/catalog';
import type { PreviewCatalogRulesResult } from '@src/apis/reware/catalog';

import type { FacetGroup } from '@src/facets';
import type { CatalogRule, ConditionItem } from './types';
import { createEmptyCondition, deepCloneRule, generateId, isConditionGroup, isFamilyField, isJsonField } from './types';
import RuleCard from './RuleCard';
import ConfirmRulesDialog from './ConfirmRulesDialog';
import SaveResultsDialog from './SaveResultsDialog';

const createEmptyRule = (): CatalogRule => ({
  id: generateId(),
  enabled: true,
  conditions: [createEmptyCondition()],
  actions: [{ id: generateId(), field: '', value: '', pairs: [] }],
});

const RULES_PER_PAGE = 20;

const CatalogRulesPage = () => {
  const { title } = useTitle('Catalog Rules');
  const notify = useNotify();

  const [rules, setRules] = useState<CatalogRule[]>([]);
  const [page, setPage] = useState(1);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [isPreviewing, setIsPreviewing] = useState(false);
  const [previewResult, setPreviewResult] = useState<PreviewCatalogRulesResult | null>(null);
  const [saveMatchedCount, setSaveMatchedCount] = useState<number | null>(null);

  const partner = getCurrentPartner();
  const { data: facetGroups = [] } = useGetList<FacetGroup>('facets');

  const loadRules = useCallback(async () => {
    if (!partner?.code) {
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    try {
      const rules = await getCatalogRules(partner.code);
      setRules(rules.length > 0 ? rules : [createEmptyRule()]);
    } catch {
      notify('Failed to load catalog rules', { type: 'error' });
      setRules([createEmptyRule()]);
    } finally {
      setIsLoading(false);
    }
  }, [partner?.code, notify]);

  useEffect(() => {
    loadRules();
  }, [loadRules]);

  const handleUpdateRule = (id: string, updates: Partial<CatalogRule>) => {
    setRules(groups =>
      groups.map(g => (g.id === id ? { ...g, ...updates } : g)),);
  };

  const handleDeleteRule = (id: string) => {
    setRules((prev) => {
      const updated = prev.filter(g => g.id !== id);
      const maxPage = Math.max(1, Math.ceil(updated.length / RULES_PER_PAGE));
      if (page > maxPage) setPage(maxPage);
      return updated;
    });
  };

  const handleAddRule = () => {
    setRules(groups => [createEmptyRule(), ...groups]);
    setPage(1);
  };

  const handleDuplicateRule = (id: string) => {
    setRules((prev) => {
      const index = prev.findIndex(r => r.id === id);
      if (index === -1) return prev;
      const clone = deepCloneRule(prev[index]);
      const updated = [...prev];
      updated.splice(index, 0, clone);
      return updated;
    });
  };

  const dragIndexRef = useRef<number | null>(null);

  const handleDragStart = (index: number) => {
    dragIndexRef.current = index;
  };

  const handleDrop = (targetIndex: number) => {
    const sourceIndex = dragIndexRef.current;
    if (sourceIndex === null || sourceIndex === targetIndex) return;
    setRules(groups => {
      const updated = [...groups];
      const [moved] = updated.splice(sourceIndex, 1);
      updated.splice(targetIndex, 0, moved);
      return updated;
    });
    dragIndexRef.current = null;
  };

  const handleDragEnd = () => {
    dragIndexRef.current = null;
  };

  const needsValue = (operator: string) => !['isEmpty', 'isNotEmpty'].includes(operator);

  const isConditionPairValid = (p: { key: string; value: string; values: string[] }, field: string) =>
    isFamilyField(field) ? p.key && p.values.length > 0 : p.key && p.value;

  const isActionPairValid = (p: { key: string; value: string; values: string[] }, field: string) =>
    isFamilyField(field) ? p.key && p.values.length > 0 : p.key && p.value;

  const isConditionItemValid = (item: ConditionItem): boolean => {
    if (isConditionGroup(item)) {
      return item.children.length > 0 && item.children.every(isConditionItemValid);
    }
    if (!item.field || !item.operator) return false;
    if (!needsValue(item.operator)) return true;
    if (isJsonField(item.field)) {
      return item.pairs.length > 0 && item.pairs.every(p => isConditionPairValid(p, item.field));
    }
    return item.value !== '';
  };

  const isRuleValid = (group: CatalogRule) => {
    const conditionsValid = group.conditions.length > 0 &&
      group.conditions.every(isConditionItemValid);
    const actionsValid = group.actions.every((a) => {
      if (!a.field) return false;
      if (isJsonField(a.field)) {
        return a.pairs.length > 0 && a.pairs.every(p => isActionPairValid(p, a.field));
      }
      return a.value !== '';
    });
    return conditionsValid && actionsValid;
  };

  const canSave = rules.length === 0 || rules.every(isRuleValid);

  const handleSaveAndApply = async () => {
    if (!partner?.code) {
      notify('Please select a partner first', { type: 'warning' });
      return;
    }

    setIsPreviewing(true);
    setIsConfirmOpen(true);
    try {
      const result = await previewCatalogRules(partner.code, rules.filter(r => r.enabled));
      setPreviewResult(result);
    } catch {
      notify('Failed to preview catalog rules', { type: 'error' });
      setIsConfirmOpen(false);
    } finally {
      setIsPreviewing(false);
    }
  };

  const handleConfirmSave = async () => {
    if (!partner?.code) return;

    setIsSaving(true);
    try {
      await saveCatalogRules(partner.code, rules);
      const matched = previewResult?.totalItemsMatched ?? 0;
      setIsConfirmOpen(false);
      setPreviewResult(null);
      setSaveMatchedCount(matched);
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      notify(`Failed to save catalog rules: ${message}`, { type: 'error' });
    } finally {
      setIsSaving(false);
    }
  };

  const handleConfirmClose = () => {
    if (!isSaving) {
      setIsConfirmOpen(false);
      setPreviewResult(null);
    }
  };

  if (isLoading) {
    return (
      <Box sx={{ p: 3, display: 'flex', justifyContent: 'center', mt: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!partner?.code) {
    return (
      <Box sx={{ p: 3, maxWidth: 1200 }}>
        <Title title={title} />
        <Typography variant="body1" color="text.secondary">
          Please select a partner to manage catalog rules.
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, maxWidth: 1200, position: 'relative' }}>
      <Title title={title} />

      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={4}>
        <Box>
          <Typography variant="h5" component="h1" fontWeight={700}>
            Catalog Rules
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Create if/then rules that modify catalog records for the selected partner.
          </Typography>
        </Box>

        <Button
          variant="contained"
          onClick={handleSaveAndApply}
          disabled={isSaving || !canSave}
          startIcon={isSaving ? <CircularProgress size={16} color="inherit" /> : undefined}
          sx={{
            bgcolor: '#c1440e',
            '&:hover': { bgcolor: '#a53b0b' },
            textTransform: 'none',
            fontWeight: 600,
            px: 3,
            whiteSpace: 'nowrap',
          }}
        >
          {isSaving ? 'Saving & Applying...' : 'Save & Apply'}
        </Button>
      </Box>

      {isSaving && (
        <Box sx={{ mb: 3 }}>
          <LinearProgress sx={{ borderRadius: 1, '& .MuiLinearProgress-bar': { bgcolor: '#c1440e' } }} />
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1, textAlign: 'center' }}>
            Saving rules and applying to catalog items. This may take a moment...
          </Typography>
        </Box>
      )}

      <Backdrop open={isSaving} sx={{ position: 'absolute', zIndex: 1, bgcolor: 'rgba(255,255,255,0.6)', borderRadius: 1 }} />

      {/* Add Rule */}
      <Button
        variant="contained"
        startIcon={<span>+</span>}
        onClick={handleAddRule}
        sx={{
          bgcolor: '#333',
          '&:hover': { bgcolor: '#555' },
          textTransform: 'none',
          fontWeight: 600,
          px: 3,
          mb: 3,
        }}
      >
        Add Rule
      </Button>

      {/* Rules */}
      {rules
        .slice((page - 1) * RULES_PER_PAGE, page * RULES_PER_PAGE)
        .map((rule, i) => {
          const globalIndex = (page - 1) * RULES_PER_PAGE + i;
          return (
            <RuleCard
              key={rule.id}
              rule={rule}
              index={globalIndex}
              facetGroups={facetGroups}
              onUpdate={handleUpdateRule}
              onDelete={handleDeleteRule}
              onDuplicate={handleDuplicateRule}
              onDragStart={handleDragStart}
              onDrop={handleDrop}
              onDragEnd={handleDragEnd}
            />
          );
        })}

      {rules.length > RULES_PER_PAGE && (
        <Box display="flex" justifyContent="center" mt={2} mb={2}>
          <Pagination
            count={Math.ceil(rules.length / RULES_PER_PAGE)}
            page={page}
            onChange={(_e, value) => setPage(value)}
            color="standard"
            shape="rounded"
          />
        </Box>
      )}

      <ConfirmRulesDialog
        open={isConfirmOpen}
        onClose={handleConfirmClose}
        onConfirm={handleConfirmSave}
        isLoading={isPreviewing}
        isSaving={isSaving}
        totalItemsMatched={previewResult?.totalItemsMatched ?? 0}
        ruleMatches={previewResult?.ruleMatches ?? []}
      />

      {saveMatchedCount !== null && (
        <SaveResultsDialog
          open
          onClose={() => setSaveMatchedCount(null)}
          totalItemsMatched={saveMatchedCount}
        />
      )}
    </Box>
  );
};

export default CatalogRulesPage;
